import { delay } from 'redux-saga'
import { all, put, call, select, takeLatest } from 'redux-saga/effects'

import { getInteractions } from '../api'
import { apiError, showPopup } from '../helpers'
import { captureException } from '../helpers/Sentry'

import { INITIALIZE_APP_SUCCESS } from './app'
import { logout, LOGOUT } from './auth'
import { fetchInteraction } from './interaction'
import { push } from 'connected-react-router'

const initialState = {
  entities: [],
  filterText: '',
  loadingSelectedTicket: true,
  error: null,
  loading: false,
  limit: 20,
  isMoreToLoad: true,
  offset: 0,
  cachedRequest: '',
  scrollToTop: false
}

export const moduleName = 'interactions'
export const FETCH_INTERACTIONS_REQUEST = `${moduleName}/FETCH_INTERACTIONS_REQUEST`
export const FETCH_INTERACTIONS_SUCCESS = `${moduleName}/FETCH_INTERACTIONS_SUCCESS`
export const FETCH_INTERACTIONS_FAIL = `${moduleName}/FETCH_INTERACTIONS_FAIL`
export const SET_FILTER_TEXT = `${moduleName}/SET_FILTER_TEXT`
export const SET_SCROLL_TOP = `${moduleName}/SET_SCROLL_TOP`
export const SET_LOADING_SCREEN_TYPE = `${moduleName}/SET_LOADING_SCREEN_TYPE`

export default function reducer(state = initialState, action) {
  const { type, payload } = action

  switch (type) {
    case FETCH_INTERACTIONS_REQUEST:
      return { ...state, loading: true, error: null }
    case FETCH_INTERACTIONS_SUCCESS:
      return {
        ...state,
        loading: false,
        error: null,
        loadingSelectedTicket: false,
        scrollToTop: false,
        ...payload
      }
    case FETCH_INTERACTIONS_FAIL:
      return { ...state, loading: false, scrollToTop: false, error: payload }

    // case SELECT_ACTIVE_STATE:
    // case FETCH_STATES_SUCCESS:
    // return { ...state, loading: true }

    case SET_FILTER_TEXT:
      return { ...state, filterText: payload }

    case SET_SCROLL_TOP:
      return { ...state, scrollToTop: true }

    case INITIALIZE_APP_SUCCESS:
      return { ...state, loading: false }

    case LOGOUT:
      return { ...initialState }

    case SET_LOADING_SCREEN_TYPE:
      return { ...state, loadingSelectedTicket: payload }

    default:
      return state
  }
}

export function setScrollToTop() {
  return {
    type: SET_SCROLL_TOP
  }
}

export function fetchInteractions(lazy = false) {
  return {
    type: FETCH_INTERACTIONS_REQUEST,
    lazy
  }
}

export function handleFilterTextChange(text, debounce) {
  return {
    type: SET_FILTER_TEXT,
    payload: text,
    debounce
  }
}

export function setLoadingScreenType(payload) {
  return {
    type: SET_LOADING_SCREEN_TYPE,
    payload
  }
}

export const handleFilterChangeSaga = function*({ debounce }) {
  try {
    if (debounce) {
      yield delay(500)
    }
    yield put({ type: FETCH_INTERACTIONS_REQUEST })
  } catch (e) {
    console.error(e)
  }
}

export const fetchInteractionsSaga = function*(
  action,
  loadingWithStatesForceChange = false
) {
  try {
    const {
      filterText,
      loadingSelectedTicket,
      limit,
      cachedRequest
    } = yield select((state) => state.interactions)
    const { width } = yield select((state) => state.ui.sizes.app)

    const router = yield select(({ router }) => router)

    let { offset, entities } = yield select((state) => state.interactions)

    const lazy = (action && action.lazy === true) || false

    if (!lazy) {
      offset = 0
      entities = []
    }

    const propertyId = yield select(
      (state) => state.properties.selectedPropertyId
    )
    const productId = yield select((state) => state.products.selectedProductId)
    const { entities: statesEntities, selectedStateIndex } = yield select(
      ({ states }) => states
    )

    const selectedState = statesEntities[selectedStateIndex].query

    const newCachedRequest = `${propertyId}|${productId}|${selectedState}|${filterText}`

    if (newCachedRequest !== cachedRequest) {
      yield put(setScrollToTop())
    }

    const response = yield call(
      getInteractions,
      propertyId,
      productId,
      selectedState,
      filterText,
      limit,
      offset
    )

    const payload = {
      offset: offset + limit,
      entities: [...entities, ...response],
      cachedRequest: newCachedRequest
    }

    payload.isMoreToLoad = response.length === limit

    yield put({
      type: FETCH_INTERACTIONS_SUCCESS,
      payload
    })

    if (router.location.pathname.indexOf('/dashboard/interactions') === -1) {
      yield put(push('/dashboard/interactions'))
    }

    const { selectedInteractionId } = yield select((state) => state.interaction)

    const loadCertainTicket =
      loadingSelectedTicket || loadingWithStatesForceChange

    if (!lazy) {
      if (width < 992 && selectedInteractionId) {
        yield put(fetchInteraction(selectedInteractionId, true))
      } else if (width >= 992) {
        if (loadCertainTicket && selectedInteractionId) {
          yield put(fetchInteraction(selectedInteractionId, true))
        } else if (response.length > 0) {
          yield put(fetchInteraction(response[0].interaction_id))
        } else {
          yield put(push('/dashboard/interactions'))
        }
      }
    }
  } catch (e) {
    yield put(showPopup(e))
    if (e instanceof apiError) {
      const { code, message } = e
      yield put({
        type: FETCH_INTERACTIONS_FAIL,
        payload: {
          code,
          message
        }
      })
      if (code === 2) {
        yield put(logout())
      }
    } else {
      yield put({
        type: FETCH_INTERACTIONS_FAIL,
        payload: {
          code: 0,
          message: 'look what I got!'
        }
      })
      // TODO call some cross-screen error
      captureException(e)
    }
  }
}

export const saga = function*() {
  yield all([
    takeLatest(FETCH_INTERACTIONS_REQUEST, fetchInteractionsSaga),
    takeLatest(SET_FILTER_TEXT, handleFilterChangeSaga)
  ])
}
